<template>
  <div class="tpl-compare-page">
    <div class="tpl-compare-toolbar">
      <div class="toolbar-left">
        <span class="toolbar-title">模板对比</span>
        <el-tag size="mini" type="info">{{ index + 1 }} / {{ items.length }}</el-tag>
        <el-tag size="mini" :type="currentItem && currentItem.sub ? 'warning' : 'success'">
          {{ displayKey }}
        </el-tag>
        <span v-if="currentItem" class="toolbar-file">{{ currentItem.filename }}</span>
      </div>
      <div class="toolbar-center">
        <el-button size="mini" :disabled="index <= 0 || loading" @click="goPrev">上一个</el-button>
        <el-select
          v-model="index"
          size="mini"
          filterable
          class="toolbar-select"
          :disabled="loading"
          @change="onIndexChange"
        >
          <el-option
            v-for="(it, i) in items"
            :key="it.key"
            :label="optionLabel(it)"
            :value="i"
          />
        </el-select>
        <el-button size="mini" :disabled="index >= items.length - 1 || loading" @click="goNext">下一个</el-button>
        <el-button size="mini" type="primary" plain :loading="loading" @click="reloadPair">刷新对比</el-button>
      </div>
      <div class="toolbar-right">
        <el-switch v-model="autoPlay" active-text="自动下一个" inactive-text="" @change="onAutoToggle" />
      </div>
    </div>

    <div v-if="listError" class="tpl-compare-error">{{ listError }}</div>

    <div v-else class="tpl-compare-panels">
      <section class="compare-panel">
        <header class="panel-head">
          <strong>hedui 原版</strong>
          <span v-if="heduiCfg && heduiCfg.filename" class="panel-sub">{{ heduiCfg.filename }}</span>
        </header>
        <OnlyOfficeEditor
          v-if="heduiCfg"
          :key="'h-' + editorKey"
          :config="heduiCfg"
          :fullscreen="false"
          class="compare-editor"
          @ready="onHeduiReady"
        />
        <div v-else-if="loading" class="panel-placeholder">加载 hedui…</div>
        <div v-else-if="heduiError" class="panel-placeholder is-error">{{ heduiError }}</div>
      </section>

      <section class="compare-panel">
        <header class="panel-head">
          <strong>tpl 系统模板</strong>
          <span v-if="tplCfg && tplCfg.filename" class="panel-sub">{{ tplCfg.filename }}</span>
        </header>
        <OnlyOfficeEditor
          v-if="tplCfg"
          :key="'t-' + editorKey"
          :config="tplCfg"
          :fullscreen="false"
          class="compare-editor"
          @ready="onTplReady"
        />
        <div v-else-if="loading" class="panel-placeholder">加载 tpl…</div>
        <div v-else-if="tplError" class="panel-placeholder is-error">{{ tplError }}</div>
      </section>
    </div>

    <div class="tpl-compare-checklist">
      <span class="check-label">检查项：</span>
      <el-tag v-for="c in checks" :key="c.key" size="mini" :type="c.type">{{ c.label }}</el-tag>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import OnlyOfficeEditor from '@/components/OnlyOfficeEditor.vue'

/** 土壤录入页「检测方法」常见勾选表（含续表） */
const METHOD_FORM_SET = [
  '005', '007', '015', '019', '019-1', '025', '028', '032', '036', '041',
  '048', '091', '092', '092-1', '104', '115', '127', '129', '130', '147',
  '148', '168', '171', '172', '186', '187', '188', '196'
]

export default {
  name: 'TemplateCompare',
  components: { OnlyOfficeEditor },
  data() {
    return {
      items: [],
      index: 0,
      loading: false,
      listError: '',
      heduiCfg: null,
      tplCfg: null,
      heduiError: '',
      tplError: '',
      editorKey: 0,
      autoPlay: false,
      autoTimer: null,
      heduiReady: false,
      tplReady: false
    }
  },
  computed: {
    currentItem() {
      return this.items[this.index] || null
    },
    displayKey() {
      const it = this.currentItem
      if (!it) return '—'
      return it.sub ? `GSYN-${it.num}-${it.sub}` : `GSYN-${it.num}`
    },
    formNumParam() {
      const it = this.currentItem
      if (!it) return ''
      return it.sub ? `${it.num}-${it.sub}` : it.num
    },
    checks() {
      const ok = !this.heduiError && !this.tplError && this.heduiReady && this.tplReady
      return [
        { key: 'load', label: ok ? '双端加载 OK' : '加载中/失败', type: ok ? 'success' : 'warning' },
        { key: 'p1', label: '分页：目视对比左右页数', type: 'info' },
        { key: 'p2', label: '重叠/边框/字体：目视对比', type: 'info' }
      ]
    }
  },
  mounted() {
    document.title = '模板对比 · hedui vs tpl'
    if (String(this.$route.query.auto || '') === '1') this.autoPlay = true
    this.bootstrap().then(() => {
      if (this.autoPlay) this.startAuto()
    })
  },
  beforeDestroy() {
    this.stopAuto()
  },
  watch: {
    '$route.query.index'(v) {
      const i = parseInt(v, 10)
      if (!Number.isNaN(i) && i >= 0 && i < this.items.length && i !== this.index) {
        this.index = i
        this.reloadPair()
      }
    },
    '$route.query.form_num'(v) {
      if (!v || !this.items.length) return
      const idx = this.items.findIndex(it => this.itemFormNum(it) === String(v))
      if (idx >= 0 && idx !== this.index) {
        this.index = idx
        this.reloadPair()
      }
    }
  },
  methods: {
    itemFormNum(it) {
      return it.sub ? `${it.num}-${it.sub}` : it.num
    },
    optionLabel(it) {
      const tag = it.sub ? `续${it.sub}` : '主'
      return `GSYN-${it.num}${it.sub ? '-' + it.sub : ''} (${tag})`
    },
    async bootstrap() {
      this.listError = ''
      try {
        const res = await api.get('yn_hedui_list')
        const data = res.data || {}
        if (data.error) {
          this.listError = data.error
          return
        }
        this.items = (data.items || []).map(it => ({
          ...it,
          key: `${it.num}${it.sub ? '-' + it.sub : ''}`
        }))
        const q = this.$route.query || {}
        if (String(q.set || '') === 'methods') {
          const allow = new Set(METHOD_FORM_SET)
          this.items = this.items.filter(it => allow.has(this.itemFormNum(it)))
        }
        if (!this.items.length) {
          this.listError = 'hedui 列表为空'
          return
        }
        if (q.form_num) {
          const idx = this.items.findIndex(it => this.itemFormNum(it) === String(q.form_num))
          if (idx >= 0) this.index = idx
        } else if (q.index != null) {
          const i = parseInt(q.index, 10)
          if (!Number.isNaN(i) && i >= 0 && i < this.items.length) this.index = i
        }
        this.syncRoute()
        await this.reloadPair()
      } catch (e) {
        this.listError = (e && e.message) || '加载 hedui 列表失败'
      }
    },
    syncRoute() {
      const q = {
        form_num: this.formNumParam,
        index: String(this.index)
      }
      if (String(this.$route.query.set || '') === 'methods') q.set = 'methods'
      if (this.autoPlay) q.auto = '1'
      this.$router.replace({ path: '/soil/template-compare', query: q }).catch(() => {})
    },
    onIndexChange() {
      this.syncRoute()
      this.reloadPair()
    },
    goPrev() {
      if (this.index <= 0) return
      this.index -= 1
      this.syncRoute()
      this.reloadPair()
    },
    goNext() {
      if (this.index >= this.items.length - 1) return
      this.index += 1
      this.syncRoute()
      this.reloadPair()
    },
    onAutoToggle(on) {
      if (on) this.startAuto()
      else this.stopAuto()
    },
    startAuto() {
      this.stopAuto()
      this.autoTimer = setInterval(() => {
        if (this.loading) return
        if (this.index >= this.items.length - 1) {
          this.autoPlay = false
          this.stopAuto()
          return
        }
        this.goNext()
      }, 12000)
    },
    stopAuto() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer)
        this.autoTimer = null
      }
    },
    onHeduiReady() {
      this.heduiReady = true
      this.tryScheduleAuto()
    },
    onTplReady() {
      this.tplReady = true
      this.tryScheduleAuto()
    },
    tryScheduleAuto() {
      if (!this.autoPlay || this.loading) return
      if (this.heduiReady && this.tplReady) {
        /* auto interval handles advance */
      }
    },
    async reloadPair() {
      if (!this.formNumParam) return
      this.loading = true
      this.heduiCfg = null
      this.tplCfg = null
      this.heduiError = ''
      this.tplError = ''
      this.heduiReady = false
      this.tplReady = false
      this.editorKey += 1
      const fn = this.formNumParam
      try {
        const [hRes, tRes] = await Promise.all([
          api.get('yn_preview_hedui', { form_num: fn }),
          api.get('yn_preview_tpl', { form_num: fn })
        ])
        const h = hRes.data || {}
        const t = tRes.data || {}
        if (h.error) this.heduiError = h.error
        else if (h.document && h.document.url) {
          this.heduiCfg = {
            documentServerUrl: h.documentServerUrl,
            document: h.document,
            editorConfig: Object.assign({ mode: 'view', lang: 'zh-CN' }, h.editorConfig || {})
          }
        } else {
          this.heduiError = 'hedui 预览配置无效'
        }
        if (t.error) this.tplError = t.error
        else if (t.document && t.document.url) {
          this.tplCfg = {
            documentServerUrl: t.documentServerUrl,
            document: t.document,
            editorConfig: Object.assign({ mode: 'view', lang: 'zh-CN' }, t.editorConfig || {})
          }
        } else {
          this.tplError = 'tpl 预览配置无效'
        }
      } catch (e) {
        this.heduiError = (e && e.message) || '加载失败'
        this.tplError = this.heduiError
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.tpl-compare-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #1a1a1a;
  color: #e8e8e8;
}
.tpl-compare-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  background: #252526;
  border-bottom: 1px solid #333;
  flex-wrap: wrap;
}
.toolbar-left, .toolbar-center, .toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar-title {
  font-weight: 600;
  font-size: 14px;
}
.toolbar-file {
  font-size: 12px;
  color: #999;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.toolbar-select {
  width: 220px;
}
.tpl-compare-panels {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  min-height: 0;
  background: #111;
}
.compare-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #2d2d2d;
}
.panel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  font-size: 13px;
  background: #333;
  border-bottom: 1px solid #444;
}
.panel-sub {
  font-size: 11px;
  color: #aaa;
}
.compare-editor {
  flex: 1;
  min-height: 0;
}
.compare-editor >>> .oo-editor-wrap,
.compare-editor >>> .oo-editor {
  height: 100% !important;
  min-height: 400px;
}
.panel-placeholder {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 14px;
}
.panel-placeholder.is-error {
  color: #f56c6c;
}
.tpl-compare-error {
  padding: 24px;
  color: #f56c6c;
  text-align: center;
}
.tpl-compare-checklist {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #252526;
  border-top: 1px solid #333;
  font-size: 12px;
}
.check-label {
  color: #999;
}
</style>
